import { inject, Injectable, InjectionToken } from '@angular/core'

import { defer, Observable, timeout } from 'rxjs'

import { environment } from '@env'

const RECAPTCHA_ACTION = 'submit'

/**
 * What `refresh` fails with when Google's script is not there (blocked, unreachable or still loading), told
 * apart from Google failing or not answering.
 */
export class RecaptchaScriptMissingError extends Error {
	constructor() {
		super('reCAPTCHA script is not loaded')
		this.name = 'RecaptchaScriptMissingError'
	}
}

/**
 * Reads Google's `grecaptcha` global at call time, since its script loads asynchronously; `undefined` when the
 * script has not loaded or a content blocker stopped it. Replaced by specs with a fake.
 */
export const GRECAPTCHA = new InjectionToken<() => Grecaptcha | undefined>('GRECAPTCHA', {
	providedIn: 'root',
	factory: () => () => (globalThis as { readonly grecaptcha?: Grecaptcha }).grecaptcha,
})

@Injectable({
	providedIn: 'root',
})
export class RecaptchaService {
	readonly #grecaptcha = inject(GRECAPTCHA)

	/**
	 * Emits a fresh token, or fails when none can be obtained within `timeoutMs`: the script is
	 * missing, Google rejects, or `ready`/`execute` never answer. Before this limit the guest waited forever on a
	 * spinner. A token that arrives after the limit is dropped, so a request already abandoned is never sent with it.
	 */
	refresh(timeoutMs: number): Observable<string> {
		return defer(() => this.#token()).pipe(timeout(timeoutMs))
	}

	#token(): Promise<string> {
		const grecaptcha = this.#grecaptcha()

		if (!grecaptcha) return Promise.reject(new RecaptchaScriptMissingError())

		return new Promise((resolve, reject) => {
			grecaptcha.ready(() => {
				// Chained so an `execute` that throws instead of rejecting also fails the request.
				Promise.resolve()
					.then(() =>
						grecaptcha.execute(environment.reCaptcha.siteKey, {
							action: RECAPTCHA_ACTION,
						})
					)
					.then(resolve, reject)
			})
		})
	}
}
